import React from 'react';
import { Form, Typography, theme } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import { DEFAULT_DURATION_MINUTES } from '../constants';

const { Text } = Typography;

function toSafeDuration(value: unknown, fallback: number): number {
  const n = Number(value);
  return (Number.isInteger(n) && n >= 5 && n <= 480) ? n : fallback;
}

/**
 * Formats a start/end pair using the active dayjs locale; the date is shown once when both
 * ends fall on the same day.
 */
function formatTimeRange(start: Dayjs, end: Dayjs): string {
  const dateFormat = 'ddd, D MMM YYYY';
  if (start.isSame(end, 'day')) {
    return `${start.format(dateFormat)}, ${start.format('HH:mm')} – ${end.format('HH:mm')}`;
  }
  return `${start.format(`${dateFormat}, HH:mm`)} – ${end.format(`${dateFormat}, HH:mm`)}`;
}

interface TimePreviewProps {
  duration: number;
}

/** Summary bar under the slot picker showing the resulting start–end range and duration. */
export const TimePreview: React.FC<TimePreviewProps> = ({ duration }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  return (
    <Form.Item shouldUpdate={(prevValues, currentValues) =>
      prevValues.startTime !== currentValues.startTime || prevValues.duration !== currentValues.duration
    }>
      {({ getFieldValue }) => {
        const startTime: Dayjs | undefined = getFieldValue('startTime');
        const raw = getFieldValue('duration') ?? duration;
        const formDuration = toSafeDuration(raw, duration || DEFAULT_DURATION_MINUTES);

        const isSelected = !!startTime && startTime.isValid();
        const calculatedEnd = isSelected ? startTime.add(formDuration, 'minute') : null;

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: token.marginXS,
              flexWrap: 'wrap',
              background: isSelected ? token.colorPrimaryBg : token.colorFillQuaternary,
              border: `1px solid ${isSelected ? token.colorPrimaryBorder : token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: `${token.paddingXS}px ${token.paddingSM}px`
            }}
          >
            {isSelected && calculatedEnd ? (
              <>
                <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                <Text style={{ fontSize: token.fontSizeSM }}>
                  {formatTimeRange(startTime, calculatedEnd)}
                </Text>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  · {t('appointmentComponents.create.service.durationTag', { count: formDuration })}
                </Text>
              </>
            ) : (
              <>
                <ClockCircleOutlined style={{ color: token.colorTextTertiary }} />
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t('appointmentComponents.create.timePreview.placeholder')}
                </Text>
              </>
            )}
          </div>
        );
      }}
    </Form.Item>
  );
};
